import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
// TODO: REMOVE/REPLACE MOMENT
import * as moment from 'moment';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Icon from '../Icon/Icon.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate from '../Translate/Translate.js';
import { TranslationContext } from '../Translate/Translate.js';

import { ICalendarPickerDayProps } from './models/ICalendarPickerDayProps.js';
import {
	ICalendarInputConfiguration,
	ICalendarInputProps,
	ICalendarInputState,
	IDayNames,
	IMonthNames,
} from './models/index.js';
import Body from './styledComponents/Body.js';
import Calendar from './styledComponents/Calendar.js';
import CalendarButton from './styledComponents/CalendarButton.js';
import ClickLayer from './styledComponents/ClickLayer.js';
import DatePickerDay from './styledComponents/DatePickerDay.js';
import DatePickerDayDisabled from './styledComponents/DatePickerDayDisabled.js';
import DatePickerDayPlaceholder from './styledComponents/DatePickerDayPlaceholder.js';
import DatePickerDaySelected from './styledComponents/DatePickerDaySelected.js';
import DatePickerDayToday from './styledComponents/DatePickerDayToday.js';
import DayOfWeek from './styledComponents/DayOfWeek.js';
import Header from './styledComponents/Header.js';
import HeaderNav from './styledComponents/HeaderNav.js';
import Month from './styledComponents/Month.js';
import MonthButtonNext from './styledComponents/MonthButtonNext.js';
import MonthButtonPrevious from './styledComponents/MonthButtonPrevious.js';
import Row from './styledComponents/Row.js';
import Wrapper from './styledComponents/Wrapper.js';
import Year from './styledComponents/Year.js';
import YearInput from './styledComponents/YearInput.js';
import { CALENDAR_INPUT_CATEGORY_KEY, CALENDAR_INPUT_COMPONENT_KEY } from './CalendarInput.meta.js';

/**
 * @deprecated
 * Use DatePicker instead for accessibility support
 */
export class CalendarInput extends ComponentBase<
	ICalendarInputProps,
	ICalendarInputState,
	ICalendarInputConfiguration
> {
	categoryKey = CALENDAR_INPUT_CATEGORY_KEY;
	componentKey = CALENDAR_INPUT_COMPONENT_KEY;

	showCalendar: boolean;
	calendarMonth: number;
	calendarYear: number;
	value: string;

	constructor(props: ICalendarInputProps) {
		super(props);

		this.value = props.value || '';
		this.showCalendar = !!props.isFlat;
		const uctValue = moment.utc(props.value);

		if (!this.getDisabled(uctValue)) {
			this.value = props.value ? uctValue.format('YYYY-MM-DD') : '';
			const calendarValue = moment.utc(this.value || Date.now());
			this.calendarMonth = parseInt(calendarValue.format('M'), 10);
			this.calendarYear = parseInt(calendarValue.format('YYYY'), 10);
		} else {
			let calendarValue = moment.utc(Date.now());
			if (this.getDisabled(calendarValue)) {
				calendarValue = moment.utc(this.maximumDate);
			}
			this.calendarMonth = parseInt(calendarValue.format('M'), 10);
			this.calendarYear = parseInt(calendarValue.format('YYYY'), 10);
			this.value = '';
		}

		makeObservable(this, {
			showCalendar: observable,
			calendarMonth: observable,
			calendarYear: observable,
			value: observable,
			monthNames: computed,
			formattedAllowedValues: computed,
			dayNames: computed,
			toggleCalendar: action,
			moveCalendarMonth: action,
			minimumDate: computed,
			maximumDate: computed,
			handleDateChange: action,
			updateCalendar: action,
		});
	}

	componentDidUpdate(previousProps: ICalendarInputProps) {
		const { value } = this.props;
		if (value !== undefined && previousProps.value !== value) {
			this.updateCalendar(value);
		}
	}

	updateCalendar(value: string) {
		const uctValue = moment.utc(value);

		if (!this.getDisabled(uctValue)) {
			this.value = uctValue.format('YYYY-MM-DD');
			const calendarValue = moment.utc(value);
			this.calendarMonth = parseInt(calendarValue.format('M'), 10);
			this.calendarYear = parseInt(calendarValue.format('YYYY'), 10);
		} else {
			let calendarValue = moment.utc(Date.now());
			if (this.getDisabled(calendarValue)) {
				calendarValue = moment.utc(this.maximumDate);
			}
			this.calendarMonth = parseInt(calendarValue.format('M'), 10);
			this.calendarYear = parseInt(calendarValue.format('YYYY'), 10);
			this.value = '';
		}
	}

	get monthNames(): IMonthNames {
		return (
			(this.props && this.props.monthNames) || {
				jan: 'January',
				feb: 'February',
				mar: 'March',
				apr: 'April',
				may: 'May',
				jun: 'June',
				jul: 'July',
				aug: 'August',
				sep: 'September',
				oct: 'October',
				nov: 'November',
				dec: 'December',
			}
		);
	}

	get formattedAllowedValues(): string[] {
		return (this.props.allowedValues || []).map((value) => moment.utc(value).format('YYYY-MM-DD'));
	}

	get dayNames(): IDayNames {
		return (
			(this.props && this.props.dayNames) || {
				sun: 'S',
				mon: 'M',
				tue: 'T',
				wed: 'W',
				thu: 'T',
				fri: 'F',
				sat: 'S',
			}
		);
	}

	toggleCalendar = (mustShow?: boolean) => {
		if (!this.props.isFlat) {
			if (common.commonUtils.isNullOrUndefined(mustShow)) {
				mustShow = !this.showCalendar;
			}
			this.showCalendar = mustShow;
		}
	};
	getValidDate(value?: string) {
		const utcDate = moment.utc(value);
		let isDisabled = !value || !utcDate.isValid() || this.getDisabled(utcDate);
		if (isDisabled) {
			isDisabled = this.getDisabled(moment.utc());

			if (isDisabled) {
				if (common.commonUtils.isNullOrEmpty(this.formattedAllowedValues)) {
					value = this.minimumDate;
				}
			} else {
				value = moment.utc().format('YYYY-MM-DD');
			}
		}
		return value as string;
	}
	getMonthName(translationContext: ITranslationContextValue) {
		const failText = 'No Month Found';
		const { jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec } = this.monthNames;
		const monthArray = [jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec];
		const monthName = common.commonUtils.safe(() => monthArray[this.calendarMonth - 1], failText);
		return monthName && translationContext.translate(monthName);
	}
	getCalendarDaysOfWeek(translationContext: ITranslationContextValue): string[] {
		const { sun, mon, tue, wed, thu, fri, sat } = this.dayNames;
		return [
			translationContext.translate(sun),
			translationContext.translate(mon),
			translationContext.translate(tue),
			translationContext.translate(wed),
			translationContext.translate(thu),
			translationContext.translate(fri),
			translationContext.translate(sat),
		];
	}
	changeCalendarYear(year: number) {
		const utcValue = moment.utc(`${year}-${this.calendarMonth?.toString().padStart(2, '0')}-01`);
		if (this.isInRange(utcValue, 'month')) {
			this.setState({ calendarYear: year });
		}
	}
	changeCalendarMonth(month: number) {
		const utcValue = moment.utc(`${this.calendarYear}-${month?.toString().padStart(2, '0')}-01`);
		if (this.isInRange(utcValue, 'month')) {
			this.setState({ calendarMonth: month });
		}
	}

	moveCalendarMonth(increment: number) {
		if (increment !== 0) {
			let calendarMonth = this.calendarMonth;
			let calendarYear = this.calendarYear;
			calendarMonth += increment;
			if (calendarMonth <= 0) {
				const yearOffset = Math.trunc(calendarMonth / 12) - 1;
				calendarMonth = 12 - (calendarMonth % 12);
				calendarYear += yearOffset;
			}
			if (calendarMonth > 12) {
				const yearOffset = Math.trunc(calendarMonth / 12);
				calendarMonth = calendarMonth % 12;
				calendarYear += yearOffset;
			}
			const utcValue = moment.utc(`${calendarYear}-${calendarMonth?.toString().padStart(2, '0')}-01`);
			if (this.isInRange(utcValue, 'month')) {
				this.calendarMonth = calendarMonth;
				this.calendarYear = calendarYear;
			}
		}
	}
	get minimumDate(): string {
		const { minimumDate } = this.props;
		if (!common.commonUtils.isNullOrEmpty(this.formattedAllowedValues)) {
			return this.formattedAllowedValues.reduce((a, b) => {
				return new Date(a) < new Date(b) ? a : b;
			});
		}
		const workingMinimumDate = minimumDate || '1970-01-01';
		return workingMinimumDate;
	}
	get maximumDate(): string {
		const { maximumDate } = this.props;
		if (!common.commonUtils.isNullOrEmpty(this.formattedAllowedValues)) {
			return this.formattedAllowedValues.reduce((a, b) => {
				return new Date(a) > new Date(b) ? a : b;
			});
		}
		const workingMaximumDate = maximumDate || '2099-01-01';
		return workingMaximumDate;
	}
	isInRange(date: moment.Moment, granularity: 'month' | 'day' = 'day') {
		let min: any = this.minimumDate;
		let max: any = this.maximumDate;
		if (granularity === 'month') {
			min = moment.default(min).startOf('month');
			max = moment.default(max).endOf('month');
		}
		return date.isBetween(min, max);
	}
	getDisabled(date: moment.Moment) {
		if (!common.commonUtils.isNullOrEmpty(this.formattedAllowedValues)) {
			return !this.formattedAllowedValues.includes(date.format('YYYY-MM-DD'));
		}
		return !this.isInRange(date);
	}
	getCalendarRows() {
		const validDate = this.getValidDate(this.value);
		const rows: ICalendarPickerDayProps[][] = [];
		const utcValue = moment.utc(`${this.calendarYear}-${this.calendarMonth?.toString().padStart(2, '0')}-01`);
		const firstDay = utcValue.startOf('month').format('YYYY-MM-DD');
		const lastDay = utcValue.endOf('month').format('YYYY-MM-DD');
		const today = this.getValidDate(moment.now().toString());

		let currentDay = moment.utc(firstDay);
		let currentRow: ICalendarPickerDayProps[] = [];
		for (let i = 0; i < parseInt(moment.utc(firstDay).format('d'), 10); i++) {
			currentRow.push({
				isPlaceholder: true,
			});
		}
		while (currentDay.isSameOrBefore(moment.utc(lastDay))) {
			if (currentRow.length === 7) {
				rows.push(currentRow);
				currentRow = [];
			}
			const date = currentDay.format('YYYY-MM-DD');
			currentRow.push({
				date,
				isPlaceholder: false,
				isSelected: date === validDate,
				isToday: date === today,
				disabled: this.getDisabled(currentDay),
			});
			currentDay = currentDay.add(1, 'day');
		}

		for (let i = parseInt(moment.utc(lastDay).format('d'), 10) + 1; i <= 6; i++) {
			currentRow.push({
				isPlaceholder: true,
			});
		}
		rows.push(currentRow);
		return rows;
	}
	handleDateChange(value: string | undefined) {
		const { onChange, allowedValues = [] } = this.props;
		if (value) {
			value = this.getValidDate(value);
			this.value = value;
			this.toggleCalendar(false);
			if (onChange) {
				if (allowedValues.length > 0) {
					const selectedValue = allowedValues.find((v) => {
						const valueAsDate = moment.utc(v).format('YYYY-MM-DD');
						return value === valueAsDate;
					});
					onChange(selectedValue || '');
				} else {
					onChange(value);
				}
			}
		}
	}
	render() {
		const { className, yearMode: headerYearMode = 'flat', suppressAnalytics = true } = this.props;
		const calendarRows = this.getCalendarRows();

		return (
			<Wrapper className={className}>
				{!this.props.isFlat && (
					<CalendarButton
						suppressAnalytics={suppressAnalytics}
						onClick={() => {
							this.toggleCalendar();
						}}
					>
						<Icon asset={'base.calendarInput.calendar'} />
					</CalendarButton>
				)}
				{(this.props.isFlat || this.showCalendar) && (
					<TranslationContext.Consumer>
						{(translationContext: ITranslationContextValue) => {
							return (
								<React.Fragment>
									{!this.props.isFlat && (
										<ClickLayer
											onClick={() => {
												this.toggleCalendar(false);
											}}
										/>
									)}
									<Calendar>
										<Header>
											<HeaderNav>
												<MonthButtonPrevious
													suppressAnalytics={suppressAnalytics}
													onClick={() => this.moveCalendarMonth(-1)}
												>
													<Icon asset={'base.calendarInput.previousMonth'} />
												</MonthButtonPrevious>
												<Month>
													<Translate>{this.getMonthName(translationContext)}</Translate>
												</Month>
												{headerYearMode === 'flat' && <Year>{this.calendarYear}</Year>}
												{headerYearMode === 'input' && (
													<YearInput
														type={'number'}
														value={this.calendarYear}
														onChange={(e: any) => {
															const calendarYear = parseInt(e.target.value, 10);
															const utcValue = moment.utc(
																`${calendarYear}-${this.calendarMonth
																	?.toString()
																	.padStart(2, '0')}-01`,
															);
															if (this.isInRange(utcValue, 'month')) {
																this.calendarYear = calendarYear;
															}
														}}
													/>
												)}
												<MonthButtonNext
													suppressAnalytics={suppressAnalytics}
													onClick={() => this.moveCalendarMonth(1)}
												>
													<Icon asset={'base.calendarInput.nextMonth'} />
												</MonthButtonNext>
											</HeaderNav>
											<Row>
												{this.getCalendarDaysOfWeek(translationContext).map(
													(dayOfWeek, index) => {
														return (
															<DayOfWeek key={`${dayOfWeek}_${index}`}>
																{dayOfWeek}
															</DayOfWeek>
														);
													},
												)}
											</Row>
										</Header>
										<Body>
											{calendarRows.map((calendarRow, index) => {
												return (
													<Row key={index}>
														{calendarRow.map((datePickerDayProps, index) => {
															const {
																date,
																isPlaceholder,
																isSelected,
																disabled,
																isToday,
															} = datePickerDayProps;
															const key = `${date}_${index}`;
															if (isPlaceholder) {
																return (
																	<DatePickerDayPlaceholder key={key}>
																		&nbsp;
																	</DatePickerDayPlaceholder>
																);
															}
															const text = moment.utc(date).format('D');
															const Component = isSelected
																? DatePickerDaySelected
																: disabled
																? DatePickerDayDisabled
																: isToday
																? DatePickerDayToday
																: DatePickerDay;
															const onClick = !disabled
																? () => {
																		this.handleDateChange(date);
																  }
																: undefined;
															return (
																<Component key={key} onClick={onClick}>
																	{text}
																</Component>
															);
														})}
													</Row>
												);
											})}
										</Body>
									</Calendar>
								</React.Fragment>
							);
						}}
					</TranslationContext.Consumer>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(CalendarInput),
	CALENDAR_INPUT_CATEGORY_KEY,
	CALENDAR_INPUT_COMPONENT_KEY,
);
